/**
 * Sync VAT with Regios Automatic Discounts (and any other on-page price rewrite)
 * without touching the cart.
 *
 * Flow:
 * 1. Liquid renders admin price + VAT from metafields.custom.vat_rate
 * 2. Regios rewrites the selling price (e.g. fixed £12) and fires
 *    `regios-dopp:price-update` with salePrice / regularPrice
 * 3. We recalculate .price__vat from the discounted unit price × vat rate
 */
(() => {
  const moneyFormat = () => window.theme?.settings?.moneyFormat || '£{{amount}}';

  const formatMoney = (cents) => {
    const format = moneyFormat();
    let amount = (cents / 100).toFixed(2);

    if (format.includes('no_decimals')) {
      amount = Math.round(cents / 100).toString();
    }

    let [whole, fraction] = amount.split('.');
    if (format.includes('with_comma_separator')) {
      whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      fraction = fraction ? `,${fraction}` : '';
    } else if (format.includes('with_space_separator')) {
      whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      fraction = fraction ? `,${fraction}` : '';
    } else if (format.includes('with_apostrophe_separator')) {
      whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
      fraction = fraction ? `.${fraction}` : '';
    } else {
      whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      fraction = fraction ? `.${fraction}` : '';
    }

    const value = fraction ? `${whole}${fraction}` : whole;
    return format.replace(/\{\{\s*amount\w*\s*\}\}/, value);
  };

  const parseMoneyToCents = (raw) => {
    if (raw == null) return null;
    const text = String(raw).replace(/<[^>]*>/g, ' ').replace(/[^\d.,]/g, '').trim();
    if (!text) return null;

    const format = moneyFormat();
    let normalized = text;

    if (format.includes('with_comma_separator')) {
      // 1.234,56
      normalized = text.replace(/\./g, '').replace(',', '.');
    } else if (format.includes('with_space_separator')) {
      // 1 234,56 (spaces already stripped above)
      normalized = text.replace(',', '.');
    } else if (format.includes('with_apostrophe_separator')) {
      // 1'234.56 (apostrophes already stripped above)
      normalized = text.replace(/,/g, '');
    } else {
      // 1,234.56
      normalized = text.replace(/,/g, '');
    }

    const amount = Number.parseFloat(normalized);
    if (Number.isNaN(amount)) return null;
    return Math.round(amount * 100);
  };

  const majorToCents = (value) => {
    if (value == null || value === '') return null;
    const amount = Number(value);
    if (Number.isNaN(amount)) return null;
    return Math.round(amount * 100);
  };

  const findPriceRoot = (fromEl) => {
    if (!(fromEl instanceof Element)) return null;
    if (fromEl.matches('.price[data-vat-rate]')) return fromEl;
    return fromEl.closest('.price[data-vat-rate]')
      || fromEl.querySelector('.price[data-vat-rate]');
  };

  const applyVat = (priceEl, priceCents) => {
    if (!priceEl || priceCents == null || priceCents < 0) return;

    const vatEl = priceEl.querySelector('.price__vat');
    if (!vatEl) return;

    const rate = Number(priceEl.dataset.vatRate);
    if (!(rate > 0)) return;

    const vatCents = Math.round((priceCents * rate) / 100);
    const vatMoney = formatMoney(vatCents);

    // Avoid observer feedback loops
    if (vatEl.dataset.vatCents === String(vatCents)) return;
    vatEl.dataset.vatCents = String(vatCents);
    vatEl.textContent = vatMoney;

    const scope = priceEl.closest('.js-product, .product-info, product-info, form, .card, .product-block');
    const vatInput = scope?.querySelector('[name="properties[VAT]"]');
    if (vatInput) vatInput.value = vatMoney;
  };

  const syncFromDisplayedPrice = (priceEl) => {
    const currentEl = priceEl.querySelector('.price__current');
    if (!currentEl) return;
    applyVat(priceEl, parseMoneyToCents(currentEl.textContent));
  };

  const observePrice = (priceEl) => {
    if (priceEl.dataset.vatSynced === 'true') return;

    const currentEl = priceEl.querySelector('.price__current');
    if (!currentEl || !priceEl.querySelector('.price__vat')) return;

    priceEl.dataset.vatSynced = 'true';
    syncFromDisplayedPrice(priceEl);

    const observer = new MutationObserver(() => syncFromDisplayedPrice(priceEl));
    observer.observe(currentEl, {
      subtree: true,
      childList: true,
      characterData: true
    });
  };

  const scan = (root = document) => {
    root.querySelectorAll?.('.price[data-vat-rate]').forEach(observePrice);
  };

  // Preferred path: Regios tells us the discounted price directly (major currency units).
  window.addEventListener('regios-dopp:price-update', (event) => {
    const detail = event.detail || {};
    const args = detail.priceUpdateArgs || {};
    const priceEl = findPriceRoot(detail.priceContainer);
    if (!priceEl) return;

    observePrice(priceEl);

    const unitPrice = detail.discountWasApplied === false
      ? args.regularPrice
      : (args.salePrice ?? args.regularPrice);

    const cents = majorToCents(unitPrice);
    if (cents != null) {
      applyVat(priceEl, cents);
      return;
    }

    // Fallback if event payload shape differs
    syncFromDisplayedPrice(priceEl);
  });

  document.addEventListener('DOMContentLoaded', () => scan());

  const bodyObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches?.('.price[data-vat-rate]')) observePrice(node);
        else scan(node);
      });
    }
  });

  if (document.body) {
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    scan();
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
